import {
  ComponentProps,
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSetAtom } from 'jotai';
import {
  Button,
  InlineNotification,
  Link,
  Tag,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import {
  Add,
  Download,
  Edit,
  Information,
  TrashCan,
} from '@carbon/react/icons';
import type { SortingState } from '@tanstack/react-table';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { DestroyConfirmation } from 'components/destroy-confirmation';
import moment from 'moment';
import { Date } from 'components/date';
import { EmptyState } from 'components/empty-state';
import { Section } from 'components/section';
import DocumentSvg from 'images/srp-illustration-document.svg';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import request from 'lib/request';
import {
  EMPTY,
  type RelatedItem,
  type RelatedItemsListResponse,
  documentHasDownload,
  relatedItemTitle,
  relatedItemTypeLabel,
  linkedByLabel,
  rowWritable,
} from './types';
import {
  RELATED_ITEMS_CHANGED_EVENT,
  deleteRelatedItemAtom,
  logRelatedItemDownload,
  relatedItemDownloadUrl,
  relatedItemWriteUrl,
  relatedItemsListUrl,
  type RelatedItemsHost,
} from './atoms';
import { subscribe } from 'lib/pusher';
import { LinkDocumentPanel } from './link-document-panel';
import { RelatedItemDetail } from './related-item-detail';
import { awaitsCatalogRecord, recordDetailHref } from './record-kind-configs';
import styles from './related-items.module.scss';

type ButtonProps = ComponentProps<typeof Button>;

// Shown when the related-items read itself fails, whatever the transport reported.
const LOAD_FAILED_MESSAGE =
  'Related items could not be loaded right now. Please try again later.';

// Related Items — every link on a host record (files, notes, links, records) in one
// server-driven table; each row expands to a per-type detail panel with row actions.

// Whether this row is the section host's own. A read-through list (the Well Workspace's
// staged well) mixes in the canonical well's items tagged `origin: canonical`; the badge
// says so. The rows keep their actions: a write on a canonical id through the workspace
// path auto-stages an overlay that Promote reconciles, so Edit and Delete only go when
// the whole section is read-only. A row with no `origin` is the host's.
const isHostOwned = (doc: RelatedItem) => doc.origin !== 'canonical';

// Column id → the attribute the endpoint sorts on. A column missing from this map
// isn't sortable: the row number is a page position, and Actions isn't data.
const SORT_ATTRIBUTES: Record<string, string> = {
  documentType: 'kind',
  title: 'title',
  linkedBy: 'created_by',
  createdAt: 'created_at',
};

// The shared table wants an index-signature row type.
export type RelatedItemRow = RelatedItem & Record<string, unknown>;

interface Props {
  /** The host record the related_items endpoints hang off. */
  host: RelatedItemsHost;
  /** Shell root the slide-in Link panel anchors its margin to. */
  selectorPageContent?: string;
  /** Hides the write actions (Link, Edit, Delete) so the section reads as a
   * view — e.g. from a promoted, read-only pre-drill workspace. Download stays. */
  readOnly?: boolean;
  /**
   * Drops the section's own "Related Items (N)" heading, for a frame that draws the
   * heading itself (the Well page's card). The header row keeps its Link Item action;
   * read-only, it renders no header at all.
   */
  hideHeading?: boolean;
  /**
   * Draws a Read-only tag in the section's own header. Opt-in because a host with a
   * card of its own says it there instead, and one that has always been read-only
   * (a promoted pre-drill workspace) reads as it always has.
   */
  showReadOnlyTag?: boolean;
}

// The upload's parse runs in a job; the list refreshes itself when the job finishes.
const processingTag = (doc: RelatedItem) => {
  if (doc.processingStatus === 'processing')
    return (
      <Tag type="blue" size="sm" title="Reading the workbook's sheets">
        Processing
      </Tag>
    );
  if (doc.processingStatus === 'failed')
    return (
      <Tag type="red" size="sm" title="The workbook's sheets could not be read">
        Extraction failed
      </Tag>
    );
  return null;
};

const renderTitleCell = (doc: RelatedItem, readOnly: boolean) => {
  const title = relatedItemTitle(doc);
  // Says where this row lives: on the well, not (yet) in this workspace — editing or
  // deleting it here stages a change against the well's item. Suppressed when the whole
  // section is read-only — a promoted workspace reads the canonical well's items only,
  // so the badge would sit on every row and distinguish nothing.
  const originTag =
    readOnly || isHostOwned(doc) ? null : (
      <Tag
        type="cool-gray"
        size="sm"
        title="Linked on the well, not in this workspace"
      >
        On well
      </Tag>
    );
  const statusTag = processingTag(doc);
  // A vesting row's url is its deed's page; a CRM tract's sends none.
  const externalHref =
    doc.kind === 'external_link' || doc.kind === 'vesting_document'
      ? doc.url
      : doc.kind === 'uploaded_file'
        ? doc.attachmentHref
        : null;
  // Record kinds link to their detail page; external links and uploads to their own.
  const href = externalHref ?? recordDetailHref(doc);

  if (href) {
    return (
      <span className={styles.titleCell}>
        <Link
          className={styles.titleLink}
          href={href}
          target="_blank"
          rel="noreferrer"
          title={title}
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          {title}
        </Link>
        {originTag}
        {statusTag}
      </span>
    );
  }

  // Notes carry no destination, so their title reads as plain text.
  return (
    <span className={styles.titleCell}>
      <span className={styles.noteTitle} title={doc.body ?? title}>
        {title}
      </span>
      {originTag}
      {statusTag}
    </span>
  );
};

/**
 * The read-only section's one header action: an info icon that says on click where
 * editing happens — the UAT wording. Hosted by the Well page card beside its collapse
 * and pin controls; the section itself never draws it, so other read-only hosts (a
 * promoted pre-drill workspace) read exactly as they always have.
 * @return {JSX.Element} The toggletip.
 */
export const RelatedItemsReadOnlyHint: FC = () => (
  <Toggletip align="bottom-right">
    <ToggletipButton label="Why can’t I edit here?">
      <Information />
    </ToggletipButton>
    <ToggletipContent>
      <p>Create a workspace to add or edit items.</p>
    </ToggletipContent>
  </Toggletip>
);

/**
 * The read-only marker a frame puts in its header — the Well Workspace card draws it
 * when the workspace is promoted, and the tract summary asks the section to draw it
 * because there is no card around it. Defined once so the two cannot drift apart.
 * @return {JSX.Element} The tag.
 */
export const RelatedItemsReadOnlyTag: FC = () => (
  <Tag type="cool-gray" size="sm">
    Read-only
  </Tag>
);

export interface RelatedItemColumnsOptions {
  basePath: string;
  page: number;
  pageSize: number;
  readOnly: boolean;
  onEdit: (doc: RelatedItem) => void;
  onDelete: (doc: RelatedItem) => void;
  /** Rows it rejects keep Edit, disabled. */
  canEdit?: (doc: RelatedItem) => boolean;
  actionTooltipPosition?: ButtonProps['tooltipPosition'];
  actionTooltipAlignment?: ButtonProps['tooltipAlignment'];
}

const DATE_FORMAT = 'MM/DD/YYYY hh:mm A';

export const relatedItemColumns = <Row extends RelatedItemRow>({
  basePath,
  page,
  pageSize,
  readOnly,
  onEdit,
  onDelete,
  canEdit,
  actionTooltipPosition = 'top',
  actionTooltipAlignment = 'end',
}: RelatedItemColumnsOptions): DataTableColumnDef<Row>[] => [
  {
    id: 'rowNumber',
    header: '#',
    size: 56,
    enableHiding: false,
    // A position in the current page, not a value — sorting it means nothing.
    enableSorting: false,
    meta: { label: '#', toggleColumnFilter: false, isExportable: false },
    cell: ({ row }) => (
      <span className={styles.tableNo}>
        {(page - 1) * pageSize + row.index + 1}
      </span>
    ),
  },
  {
    id: 'documentType',
    accessorFn: (row) => relatedItemTypeLabel(row),
    header: 'Type',
    size: 200,
  },
  {
    id: 'title',
    accessorFn: (row) => relatedItemTitle(row),
    header: 'Name / Title',
    size: 320,
    cell: ({ row }) => renderTitleCell(row.original, readOnly),
  },
  {
    id: 'linkedBy',
    accessorKey: 'createdBy',
    header: 'Linked By',
    size: 160,
    meta: { getExportValue: (row) => linkedByLabel(row as Row) },
    cell: ({ row }) => linkedByLabel(row.original),
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Date',
    size: 180,
    meta: {
      getExportValue: (row) => {
        const { createdAt } = row as Row;
        return createdAt ? moment(createdAt).format(DATE_FORMAT) : '';
      },
    },
    cell: ({ row }) => (
      <Date
        date={row.original.createdAt}
        format={DATE_FORMAT}
        fallback={EMPTY}
      />
    ),
  },
  {
    id: 'actions',
    header: 'Actions',
    size: 120,
    enableHiding: false,
    enableSorting: false,
    meta: {
      label: 'Actions',
      align: 'right',
      toggleColumnFilter: false,
      isExportable: false,
    },
    cell: ({ row }) => {
      const doc = row.original;
      const writable = !readOnly && rowWritable(doc);
      const tooltip = {
        tooltipPosition: actionTooltipPosition,
        tooltipAlignment: actionTooltipAlignment,
      };
      return (
        <div
          className={styles.actionButtons}
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          {writable && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Edit}
              iconDescription="Edit"
              {...tooltip}
              disabled={canEdit?.(doc) === false}
              onClick={() => onEdit(doc)}
            />
          )}
          {documentHasDownload(doc) && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Download}
              iconDescription="Download"
              {...tooltip}
              href={relatedItemDownloadUrl({ basePath }, doc)}
              target="_blank"
              rel="noreferrer"
              // The anchor serves the file; this records the audit event on
              // the real click (the GET href stays side-effect-free).
              onClick={() => logRelatedItemDownload({ basePath }, doc)}
            />
          )}
          {writable && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={TrashCan}
              iconDescription="Delete"
              {...tooltip}
              onClick={() => onDelete(doc)}
            />
          )}
        </div>
      );
    },
  },
];

export const RelatedItemsSection: FC<Props> = ({
  host,
  selectorPageContent = '#main-content',
  readOnly = false,
  showReadOnlyTag = false,
  hideHeading = false,
}) => {
  const basePath = host.basePath;
  const deleteDocument = useSetAtom(deleteRelatedItemAtom);

  const [items, setItems] = useState<RelatedItemRow[]>([]);
  // `totalCount` is the filtered total: it drives the header count and the pager.
  const [totalCount, setTotalCount] = useState(0);
  // Starts true so the first paint shows the table's progress bar rather than
  // flashing the empty state before the initial fetch runs.
  const [loading, setLoading] = useState(true);

  // The toolbar search input debounces on its own, so `text` is already the
  // settled query — it goes straight to the server.
  const [text, setText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [reloadNonce, setReloadNonce] = useState(0);

  // The table sorts one column at a time; an unsortable column maps to nothing
  // and the endpoint falls back to its default order.
  const activeSort = sorting[0];
  const sortAttribute = activeSort ? SORT_ATTRIBUTES[activeSort.id] : undefined;
  const sortDirection = activeSort?.desc ? 'DESC' : 'ASC';

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<'add' | 'edit'>('add');
  const [panelDocument, setPanelDocument] = useState<RelatedItem | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<RelatedItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const reload = useCallback(() => setReloadNonce((n) => n + 1), []);

  // Server-driven list; refetches on host/page/size/query/reload changes. Depends on
  // `basePath` (a string), not the `host` object, so it doesn't refire every render.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = relatedItemsListUrl(
      { basePath },
      { page, pageSize, text, sortAttribute, sortDirection }
    );
    request
      .get<RelatedItemsListResponse>(url)
      .then((res) => {
        if (cancelled) return;
        // A failed list must not read as an empty one: blank the rows AND the
        // count, and say so.
        if ('error' in res) {
          setItems([]);
          setTotalCount(0);
          // The transport's word for a failed read ("Unknown", "Internal Server Error")
          // is not a message for the reader; say what happened in plain terms.
          setActionError(LOAD_FAILED_MESSAGE);
          return;
        }
        setItems((res.relatedItems ?? []) as RelatedItemRow[]);
        setTotalCount(res.totalCount ?? 0);
        setActionError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setTotalCount(0);
        setActionError(LOAD_FAILED_MESSAGE);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    basePath,
    page,
    pageSize,
    text,
    sortAttribute,
    sortDirection,
    reloadNonce,
  ]);

  // Refresh when the host says its links changed, so a second tab (or another
  // user) editing the same record doesn't leave this list stale.
  const channel = host.notifications?.channel;
  const listensForChanges = host.notifications?.events?.some(
    (event) => event.name === RELATED_ITEMS_CHANGED_EVENT
  );
  useEffect(() => {
    if (!channel || !listensForChanges) return () => void 0;

    const subscription = subscribe(channel);
    subscription.bind(RELATED_ITEMS_CHANGED_EVENT, reload);
    // Unbind only our handler — never unsubscribe the channel. It's shared with
    // sibling sections (e.g. Parties), and unsubscribe() drops their bindings too.
    return () => subscription.unbind(RELATED_ITEMS_CHANGED_EVENT, reload);
  }, [channel, listensForChanges, reload]);

  const openAddPanel = () => {
    setPanelMode('add');
    setPanelDocument(null);
    setPanelOpen(true);
  };

  const openEditPanel = useCallback((doc: RelatedItem) => {
    setPanelMode('edit');
    setPanelDocument(doc);
    setPanelOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError(null);
    deleteDocument({
      url: relatedItemWriteUrl({ basePath }, deleteTarget),
      method: 'DELETE',
      onSuccess: () => {
        setDeleting(false);
        setDeleteTarget(null);
        // Removing the last row on a non-first page steps back a page (which
        // refetches); otherwise refetch the current page.
        if (items.length === 1 && page > 1) setPage((p) => p - 1);
        else reload();
      },
      onError: (m) => {
        setDeleting(false);
        setActionError(m ?? 'Failed to delete item.');
      },
    });
  };

  const openDelete = useCallback((doc: RelatedItem) => {
    setActionError(null);
    setDeleteTarget(doc);
  }, []);

  const columns = useMemo(
    () =>
      relatedItemColumns<RelatedItemRow>({
        basePath,
        page,
        pageSize,
        readOnly,
        onEdit: openEditPanel,
        onDelete: openDelete,
      }),
    [basePath, page, pageSize, openEditPanel, openDelete, readOnly]
  );

  // Nothing linked yet (as opposed to a search that matched nothing, which the
  // table reports itself) — swap the table for the shared empty state.
  const showEmptyState = !loading && items.length === 0 && !text;

  return (
    <Section
      header={hideHeading ? undefined : `Related Items (${totalCount})`}
      headerActions={
        // Read-only: no Link Item and no other header action, as every read-only host
        // has always read (a promoted pre-drill workspace among them). The Well page's
        // card, whose read-only-ness is the one that needs explaining, hosts
        // RelatedItemsReadOnlyHint in its own header instead.
        readOnly ? (
          showReadOnlyTag ? (
            <RelatedItemsReadOnlyTag />
          ) : undefined
        ) : (
          <div className={styles.headerActions}>
            <Button
              kind="primary"
              size="sm"
              renderIcon={Add}
              onClick={openAddPanel}
            >
              Link Item
            </Button>
          </div>
        )
      }
    >
      <div className={styles.overviewBody}>
        {actionError && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={actionError}
            onCloseButtonClick={() => setActionError(null)}
          />
        )}

        {showEmptyState ? (
          <EmptyState
            icon={DocumentSvg}
            headerText="No Related Items"
            helperText="Nothing has been linked to this record yet."
          />
        ) : (
          <DataTable<RelatedItemRow>
            data={items}
            columns={columns}
            // Two id sequences meet in this table: related_items.id and, for a
            // workbook row, external_title_supports.id. 67 values already overlap
            // in the dev mirror, and a collision shares one expansion entry.
            getRowId={(row) => `${row.kind}-${row.id}`}
            isLoading={loading}
            persistenceKey="related-items-table"
            // Sorting is server-side; the endpoint takes no per-column filter
            // params, so that one stays off rather than dangling as dead UI.
            config={{ sorting: true, columnFilters: false }}
            controlledState={{ pagination: { page, pageSize }, sorting }}
            scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
            searchPlaceholder="Search related items…"
            onPaginationChange={(state: TableState) => {
              setPage(state.page);
              setPageSize(state.pageSize);
            }}
            onSortChange={(state: TableState) => {
              setSorting(state.sorting);
              setPage(1);
            }}
            onGlobalSearchChange={(state: TableState) => {
              setText(state.globalFilter);
              setPage(1);
            }}
            // A provisioned afe/DO/RC upload is a plain hyperlink until auto-connect
            // gives it a catalog record; only then does it expand to a detail panel.
            getRowCanExpand={(row) => !awaitsCatalogRecord(row.original)}
            renderRowSubComponent={(row) => (
              <RelatedItemDetail
                document={row.original}
                readOnly={readOnly}
                onChanged={reload}
              />
            )}
          />
        )}
      </div>

      <LinkDocumentPanel
        open={panelOpen}
        mode={panelMode}
        host={host}
        selectorPageContent={selectorPageContent}
        document={panelDocument}
        onClose={() => setPanelOpen(false)}
        onSaved={() => {
          setPanelOpen(false);
          reload();
        }}
        onRefresh={() => reload()}
      />

      <DestroyConfirmation
        open={deleteTarget !== null}
        modalHeading={
          deleteTarget?.kind === 'external_title_support'
            ? 'Unlink workbook'
            : 'Delete Related Item'
        }
        size="sm"
        primaryButtonDisabled={deleting}
        preventCloseOnClickOutside={deleting}
        onRequestClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        onRequestSubmit={handleConfirmDelete}
      >
        {/*
          A workbook is unlinked, not deleted: the row keeps an `unlinked_at`
          stamp, the file and its sheets stay, and linking it again revives the
          same row. Saying "cannot be undone" is false on both counts.
        */}
        {deleteTarget?.kind === 'external_title_support' ? (
          <p>
            Remove <strong>{relatedItemTitle(deleteTarget)}</strong> from this
            tract? The workbook stays in Insights and you can link it again
            later.
          </p>
        ) : (
          <p>
            Are you sure you want to delete{' '}
            <strong>
              {deleteTarget ? relatedItemTitle(deleteTarget) : ''}
            </strong>
            ? This action cannot be undone.
          </p>
        )}
      </DestroyConfirmation>
    </Section>
  );
};
